import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Picture } from '../Picture/Picture.js';
import type { PictureConfig } from '../Picture/Picture.js';

import './DeviceDetailsPictureCoCo.scss';

export interface PublicDevicePictureData {
  id: string;
  name: string;
  src: string;
}

const PUBLIC_DEVICE_DETAILS_PICTURE_CLASS = 'of-public-device-details-picture';

export const PUBLIC_DEVICE_DETAILS_WIDTH_ALTERNATIVES: PictureConfig['offerWidthAlternatives'] = [45, 110, 275, 450];

export const DUMMY_DEVICE_PICTURES: PublicDevicePictureData[] = [
  {
    id: 'dummy-picture-1',
    name: 'Device image 1',
    src: 'https://static.elisa.com/v2/image/2tqybbhjs47b/2qvxv0jIrdMiruQr8wIHH3/Apple-iPhone-15-Pro-Max-white-Titanium-combo.png',
  },
  {
    id: 'dummy-picture-2',
    name: 'Device image 2',
    src: 'https://static.elisa.com/v2/image/2tqybbhjs47b/3zJuHaXhj8lODan1ue7fcy/elisa_ya_yrittajaliittymat_hero_kukkakauppa_1500x600.png',
  },
  {
    id: 'dummy-picture-3',
    name: 'Device image 3',
    src: 'https://static.elisa.com/v2/image/2tqybbhjs47b/6Hj2uUEJHNcnMgXZf0q0xj/elisa_ya_yrittajaliittymat_hero_puuseppa_1500x600.png',
  },
];

interface PublicDeviceDetailsPictureProps {
  pictures?: PublicDevicePictureData[];
}

export const DeviceDetailsPicture = ({ pictures = DUMMY_DEVICE_PICTURES }: PublicDeviceDetailsPictureProps) => {
  const [activePictureId, setActivePictureId] = useState<string | undefined>(pictures[0]?.id);

  useEffect(() => {
    const initialPicture = pictures[0];
    if (!initialPicture) {
      setActivePictureId(undefined);
      return;
    }

    const exists = pictures.some(picture => picture.id === activePictureId);
    if (!exists) {
      setActivePictureId(initialPicture.id);
    }
  }, [pictures, activePictureId]);

  if (!pictures.length) {
    return null;
  }

  const firstPicture = pictures[0];
  const activePicture = pictures.find(picture => picture.id === activePictureId) ?? firstPicture;
  if (!activePicture) {
    return null;
  }

  return (
    <div className={PUBLIC_DEVICE_DETAILS_PICTURE_CLASS}>
      <CL.Grid>
        <CL.GridRow>
          <CL.GridCol colWidthL={3} colWidthS={1} colWidthXS={1}>
            {pictures.map(picture => {
              const isActive = activePicture.id === picture.id;

              return (
                <div
                  key={picture.id}
                  className={classNames(
                    `${PUBLIC_DEVICE_DETAILS_PICTURE_CLASS}__side-container`,
                    isActive ? `${PUBLIC_DEVICE_DETAILS_PICTURE_CLASS}__side-container--selected` : ''
                  )}
                >
                  <div className={`${PUBLIC_DEVICE_DETAILS_PICTURE_CLASS}__side-container-picture`}>
                    <Picture
                      alt={picture.name}
                      src={picture.src}
                      offerWidthAlternatives={PUBLIC_DEVICE_DETAILS_WIDTH_ALTERNATIVES}
                      renderedImageSize={{
                        onPhone: 'calc(90vw * 0.1666 - 10px)',
                        onTablet: 'calc(90vw * 0.8333 * 0.9167 * 0.1666 - 10px)',
                        onLaptop: '120px',
                      }}
                      square
                      handlers={
                        isActive
                          ? {}
                          : {
                              onMouseEnter: () => setActivePictureId(picture.id),
                              onTouchEnd: () => setActivePictureId(picture.id),
                            }
                      }
                    />
                  </div>
                </div>
              );
            })}
          </CL.GridCol>

          <CL.GridCol
            className={`${PUBLIC_DEVICE_DETAILS_PICTURE_CLASS}__main-container`}
            colWidthL={9}
            colWidthS={5}
            colWidthXS={3}
          >
            <div className="square-container">
              <div className="square-container__wrapper">
                <Picture
                  key={activePicture.id}
                  alt={activePicture.name}
                  src={activePicture.src}
                  offerWidthAlternatives={[300, 450, 600, 750, 900, 1050, 1120]}
                  renderedImageSize={{
                    onPhone: '72vw',
                    onPhoneLarge: '70vw',
                    onTablet: 'calc(min(1400px, 100vw) * 0.9 * 0.9167 * 0.8333 - 20px)',
                    onLaptop: 'calc(min(1400px, 100vw) * 0.9 * 0.5833 * 0.6666)',
                  }}
                  square
                />
              </div>
            </div>
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>
    </div>
  );
};
